import { inject } from "mobx-react";
import * as React from "react";
import { Fragment, useMemo } from "react";

import type { BlockInterface } from "@ihr-radioedit/inferno-core";
import type { FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import { useHistory } from "react-router-dom";
import { Heading } from "../../../../core/ui";
import { buildGuestsPath, getFeedSubtitle } from "../../lib/coastFeed";
import { CoastAlphabeticalSelector } from "./coast-feed-navigation/CoastFeedAlphabeticalSelector.component";
import { CoastFeedNavigation } from "./coast-feed-navigation/CoastFeedNavigation.component";
import { CoastFeedAds } from "./CoastFeedAds.component";
import { CoastFeedGuestItem } from "./CoastFeedGuestItem.component";
import { CoastFeedNoResults } from "./CoastFeedNoResults.component";
import type { Store } from "@inferno/renderer-shared-core";

interface CoastFeedGuestsProps {
  content: FeedResultFragment[] | null;
  block: BlockInterface;
  store?: Store;
  title: string;
}

export const CoastFeedGuests = inject("store")(({ store, content, block, title }: CoastFeedGuestsProps) => {
  if (!store) {
    return null;
  }
  const history = useHistory();

  const { routeParams } = store;
  const selectedLetter = routeParams.slug?.toLowerCase() ?? "";

  const subtitle = useMemo(() => getFeedSubtitle(store.routeParams), [store.routeParams]);

  const handleChange = useMemo(
    () => (e: React.FormEvent<HTMLSelectElement>) => {
      const value = e.currentTarget.value;

      const path = buildGuestsPath({
        slug: value,
        category: routeParams.coastCategory,
        year: routeParams.year,
        month: routeParams.month,
      });

      history.push(path);
    },
    [routeParams.coastCategory, routeParams.year, routeParams.month, history],
  );

  return (
    <Fragment>
      <Heading level={2}>{title}</Heading>
      <Heading level={3}>{subtitle}</Heading>

      <CoastFeedNavigation displayHostCategories={false}>
        <CoastAlphabeticalSelector handleChange={handleChange} selectedLetter={selectedLetter} />
      </CoastFeedNavigation>

      {content?.length ? (
        <div className="feed-cards">
          {content.map((item, itemIdx) => (
            <Fragment key={item.id}>
              <CoastFeedGuestItem item={item} />
              <CoastFeedAds
                blockSize={block.value.size}
                itemCount={content.length}
                includeItemAd={false}
                blockId={block.id}
                itemIdx={itemIdx}
              />
            </Fragment>
          ))}
        </div>
      ) : (
        <CoastFeedNoResults />
      )}
    </Fragment>
  );
});

export default CoastFeedGuests;
